/* eslint-disable complexity */
import { IssueDetailList } from '@strata/react-status-icons';
import { ReferralTimeline, sortTimelineSectionsForGroup } from '@strata/react-timeline';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetReferralMilestonesQuery } from 'store/api/clientApi';
import { useGetAllSuppliersQuery, useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { fetchReferralBids } from 'store/referrals/referralsSlice';

const checkIfMilestoneFound = (milestones, milestoneType, milestoneToMatch) => {
    const foundMilestone = milestones.find((m) => m.milestone_type === milestoneType);
    let matchesType;
    if (foundMilestone) {
        matchesType =
            foundMilestone.additional_data?.supplier_npi &&
            milestoneToMatch.additional_data?.supplier_npi &&
            foundMilestone.additional_data.supplier_npi ===
                milestoneToMatch.additional_data.supplier_npi;
    }
    return matchesType;
};

export const enrichMilestones = ({ milestones, supplierMap, payerSegmentMap, referralBids }) =>
    milestones
        ? [...milestones].sort(sortTimelineSectionsForGroup).map((milestone, index, elements) => {
              if (milestone.milestone_type === ReferralTimeline.MilestoneTypes.AT_MARKETPLACE) {
                  return {
                      ...milestone,
                      additional_data: {
                          ...milestone.additional_data,
                          highestBid: (() => {
                              if (referralBids?.length > 0) {
                                  const sortedBids = [...referralBids].sort((a, b) =>
                                      a.bidAmt === b.bidAmt
                                          ? dayjs(a.bidTimestamp).subtract(a.bidTimestamp)
                                          : a.bidAmt - b.bidAmt,
                                  );
                                  return sortedBids?.[0];
                              }
                              return undefined;
                          })(),
                      },
                  };
              }
              if (
                  milestone.milestone_type ===
                  ReferralTimeline.MilestoneTypes.REFERRAL_OPPORTUNITY_INVALIDATED
              ) {
                  return {
                      ...milestone,
                      additional_data: {
                          ...milestone.additional_data,
                          issues: IssueDetailList({
                              issues: Object.keys(milestone.additional_data.issues),
                          }),
                      },
                  };
              }
              if (milestone.milestone_type === ReferralTimeline.MilestoneTypes.TRANSITION) {
                  // Replace the NPI in the title with the supplier name
                  return {
                      ...milestone,
                      title: milestone.title.replace(
                          milestone.additional_data.new_supplier_npi,
                          supplierMap?.[milestone.additional_data.new_supplier_npi]?.name,
                      ),
                  };
              }
              if (
                  milestone.milestone_type ===
                  ReferralTimeline.MilestoneTypes.TREATMENT_SEGMENT_SWITCHED
              ) {
                  return {
                      ...milestone,
                      additional_data: {
                          ...milestone.additional_data,
                          new_segment_name:
                              payerSegmentMap?.[milestone.additional_data?.new_segment_id]
                                  ?.segment_name,
                      },
                  };
              }
              if (
                  milestone.milestone_type ===
                  ReferralTimeline.MilestoneTypes.POST_LIVE_REFERRAL_ASSIGNMENT
              ) {
                  const supplier = supplierMap?.[milestone.additional_data?.supplier_npi ?? ''];
                  return {
                      ...milestone,
                      additional_data: {
                          ...milestone.additional_data,
                          supplier_name: supplier?.name,
                          network_status: supplier?.networkStatus,
                      },
                  };
              }
              if (
                  milestone.milestone_type ===
                  ReferralTimeline.MilestoneTypes.REFERRAL_STATUS_CHANGE
              ) {
                  const supplier = supplierMap?.[milestone.additional_data?.supplier_npi ?? ''];

                  const isActiveMilestone = index === elements.length - 1;
                  const nextTwoMilestones = elements.slice(index + 1, index + 3);
                  const matchesAtPharm = checkIfMilestoneFound(
                      nextTwoMilestones,
                      ReferralTimeline.MilestoneTypes.AT_PHARMACY_WON,
                      milestone,
                  );
                  const matchesDeclined = checkIfMilestoneFound(
                      nextTwoMilestones,
                      ReferralTimeline.MilestoneTypes.REFERRAL_EXCEPTION_EVENT,
                      milestone,
                  );
                  let status;
                  if (matchesAtPharm) {
                      status = 'ACCEPTED';
                  } else if (matchesDeclined) {
                      status = 'DECLINED';
                  } else {
                      status = 'COMPLETED';
                  }

                  return {
                      ...milestone,
                      additional_data: {
                          ...milestone.additional_data,
                          supplier_name: supplier?.name,
                          network_status: supplier?.networkStatus,
                          status: !isActiveMilestone ? status : undefined,
                      },
                  };
              }
              return milestone;
          })
        : undefined;

export const useEnrichedReferralMilestones = ({ payerId, referralId, referralDetails }) => {
    const dispatch = useDispatch();

    const { data: referralMilestones, isFetching: milestonesFetching } =
        useGetReferralMilestonesQuery({ payerId, referralId });

    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};

    const { data: segments } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segments ?? {};

    const referralBids = useSelector((state) => state.referrals.bids[referralId]);
    useEffect(() => {
        if (referralDetails?.referralId) {
            dispatch(
                fetchReferralBids({
                    referralId: referralDetails.referralId,
                    payerId: referralDetails.payerId,
                    auctionStart: referralDetails.auctionStart,
                    auctionEnd: referralDetails.auctionEnd,
                }),
            );
        }
    }, [dispatch, referralDetails]);

    const enrichedMilestones = enrichMilestones({
        milestones: referralMilestones,
        supplierMap,
        payerSegmentMap,
        referralBids,
    });

    return { enrichedMilestones, milestonesFetching };
};
